import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Sun, Moon, Eye, EyeOff, Shield, ClipboardCheck } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c2cdf81f-38d8-495b-bbbc-bf9142927afb/artifacts/pll87efh_ChatGPT%20Image%20Jan%2013%2C%202026%2C%2007_06_32%20AM.png";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const signedInUser = await login(loginEmail, loginPassword);
      if (signedInUser?.must_change_password) {
        toast.info('Please choose a new password to continue.');
        navigate('/change-password', { replace: true });
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex w-full flex-col justify-center px-12 xl:px-20">
          <div className="mb-8 flex justify-center">
            <img src={LOGO_URL} alt="Infinit-Audit" className="h-48 w-auto object-contain xl:h-56" />
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Food Safety Auditing<br />Made Simple
          </h1>
          <p className="text-teal-100 text-lg mb-10 max-w-md">
            Streamline your food safety compliance with our comprehensive auditing platform. Create, run, and track audits with ease.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-white">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <span>Create customizable audit templates</span>
            </div>
            <div className="flex items-center gap-4 text-white">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <span>Ensure compliance with industry standards</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-background">
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-3 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          data-testid="theme-toggle-login"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5 text-foreground" /> : <Moon className="w-5 h-5 text-foreground" />}
        </button>

        <div className="lg:hidden absolute top-4 left-1/2 -translate-x-1/2">
          <img src={LOGO_URL} alt="Infinit-Audit" className="h-24 w-auto object-contain" />
        </div>

        <Card className="w-full max-w-md border-border shadow-lg bg-card">
          <CardHeader className="text-center space-y-2 pb-2">
            <CardTitle className="text-2xl font-bold text-foreground">Welcome Back</CardTitle>
            <CardDescription className="text-muted-foreground">Sign in to access your audits</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-foreground">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  className="bg-background border-input text-foreground"
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="login-password" className="text-foreground">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="bg-background border-input text-foreground pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </div>
                ) : 'Sign In'}
              </Button>
            </form>

            <div className="mt-5 rounded-md bg-muted/50 p-3 text-center text-xs text-muted-foreground">
              New accounts are created by your Infinit Audit administrator and temporary sign-in details are sent by email.
            </div>

            <p className="text-center text-xs text-muted-foreground mt-5">
              Read our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;